import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { CheckSettingsFormValues } from '../../MultiEditCheckSettings/types'
import {
  SharedSupportCompressionSettings,
  toggleAllExcludedSupports,
  toggleExcludedSupport,
  toggleExcludedSupportIndex,
} from '../utils'

interface Result {
  /** Takes one support out of the edit, or puts a removed one back in. */
  toggleSupport: (elementGuid: string, supportIndex: number) => void
  /** Does the same for one support of every selected member at once. */
  toggleSupportColumn: (supportIndex: number) => void
  /** Takes every support out of the edit, or puts them all back. */
  toggleAllSupports: () => void
}

/**
 * The single writer of the removed supports, shared by the matrix and the scene
 * checkboxes. Writing to `sharedSettings` marks the form dirty, which is what
 * makes the preview recompute and the unsaved-changes block kick in.
 */
const useToggleExcludedSupports = (): Result => {
  const { getValues, setValue } =
    useFormContext<CheckSettingsFormValues<SharedSupportCompressionSettings>>()

  const toggleSupport = useCallback(
    (elementGuid: string, supportIndex: number) => {
      const shared = getValues('sharedSettings') as SharedSupportCompressionSettings | undefined

      setValue(
        'sharedSettings.excludedSupports',
        toggleExcludedSupport(shared?.excludedSupports, elementGuid, supportIndex),
        { shouldDirty: true, shouldTouch: true },
      )
    },
    [getValues, setValue],
  )

  const toggleSupportColumn = useCallback(
    (supportIndex: number) => {
      // The whole staged object, not just its removals: the toggle has to keep
      // the other columns' removals and skip the non-applicable supports.
      const shared = getValues('sharedSettings') as SharedSupportCompressionSettings | undefined

      setValue(
        'sharedSettings.excludedSupports',
        toggleExcludedSupportIndex(shared, getValues('elements'), supportIndex),
        { shouldDirty: true, shouldTouch: true },
      )
    },
    [getValues, setValue],
  )

  const toggleAllSupports = useCallback(() => {
    const shared = getValues('sharedSettings') as SharedSupportCompressionSettings | undefined

    setValue(
      'sharedSettings.excludedSupports',
      toggleAllExcludedSupports(shared, getValues('elements')),
      { shouldDirty: true, shouldTouch: true },
    )
  }, [getValues, setValue])

  return { toggleSupport, toggleSupportColumn, toggleAllSupports }
}

export default useToggleExcludedSupports
